import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Star, 
  Phone,
  Globe,
  Clock,
  Tag,
  ArrowLeft,
  NavigationArrow
} from '@phosphor-icons/react';
import { getWinery } from '@/lib/api';
import { Button } from '@/components/ui/button';
import ViatorWidget from '@/components/ViatorWidget';

const WineryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [winery, setWinery] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWinery = async () => {
      try {
        const response = await getWinery(id);
        setWinery(response.data);
      } catch (error) {
        console.error('Error fetching winery:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWinery();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!winery) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center">
        <h1 className="font-serif text-2xl text-primary mb-4">Winery Not Found</h1>
        <button onClick={() => navigate(-1)} className="btn-primary">
          <ArrowLeft size={18} className="mr-2" />
          Back to Wineries
        </button>
      </div>
    );
  }

  const getDirectionsUrl = () => {
    const address = encodeURIComponent(`${winery.address}, ${winery.city}, CA`);
    return `https://www.google.com/maps/dir/?api=1&destination=${address}`;
  };

  return (
    <>
      <Helmet>
        <title>{`${winery?.name || 'Winery'} - Winery Finder App | ${winery?.city || ''}, ${winery?.region || 'California'}`}</title>
        <meta name="description" content={`Visit ${winery?.name || 'this winery'} in ${winery?.city || 'California'}. ${winery?.description || 'Discover wine tasting experiences and deals.'}`} />
        <meta property="og:title" content={`${winery?.name || 'Winery'} - Winery Finder App`} />
        <meta property="og:description" content={winery?.description || `Visit ${winery?.name || 'this winery'} in ${winery?.city || ''}, ${winery?.region || 'California'}`} />
        <meta property="og:image" content={winery?.image_url || 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1200'} />
        <link rel="canonical" href={`https://www.wineryfinderapp.com/wineries/${winery?.id || id}`} />
      </Helmet>

      {/* Hero Image */}
      <section className="relative h-[40vh] sm:h-[50vh] min-h-[280px] sm:min-h-[400px]" data-testid="winery-detail-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7)), url('${winery.image_url || 'https://images.unsplash.com/photo-1506377247377-2a5b3b417ebb?w=1920'}')`
          }}
        />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 sm:pb-12 w-full">
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-3 sm:mb-4 transition-colors cursor-pointer bg-transparent border-0 text-sm"
              data-testid="back-to-wineries"
            >
              <ArrowLeft size={16} />
              Back to Wineries
            </button>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                {winery.has_deal && (
                  <span className="bg-gold-500 text-white text-xs sm:text-sm font-bold px-3 sm:px-4 py-1 rounded-full flex items-center gap-1">
                    <Tag size={12} weight="fill" />
                    Deal Available
                  </span>
                )}
                {winery.appointment_required ? (
                  <span className="bg-white/20 text-white text-xs sm:text-sm px-3 sm:px-4 py-1 rounded-full">
                    Appointment Required
                  </span>
                ) : (
                  <span className="bg-green-500/80 text-white text-xs sm:text-sm px-3 sm:px-4 py-1 rounded-full">
                    Walk-ins Welcome
                  </span>
                )}
              </div>
              <h1 className="font-serif text-2xl sm:text-4xl md:text-5xl text-white mb-2 sm:mb-3">{winery.name}</h1>
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-white/80 text-xs sm:text-base">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <MapPin size={14} className="sm:w-[18px] sm:h-[18px]" />
                  {winery.city}, {winery.region}
                </div>
                {winery.rating && (
                  <div className="flex items-center gap-1">
                    <Star size={14} weight="fill" className="text-gold-400 sm:w-[18px] sm:h-[18px]" />
                    <span className="font-medium text-white">{winery.rating}</span>
                    <span>({winery.review_count} reviews)</span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-6 sm:py-12" data-testid="winery-detail-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {winery.description && (
                <div className="mb-8">
                  <h2 className="font-serif text-2xl text-primary mb-4">About This Winery</h2>
                  <p className="text-foreground/80 leading-relaxed">{winery.description}</p>
                </div>
              )}

              {winery.has_deal && (
                <div className="bg-[#f8f5f2] rounded-xl p-5 sm:p-8 mb-8 shadow-sm">
                  <h3 className="font-serif text-xl sm:text-2xl text-[#722F37] mb-2 sm:mb-3">Special Wine Tasting Deal</h3>
                  <p className="text-gray-500 text-base sm:text-lg mb-4 sm:mb-6">Available via the Winery Finder App</p>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <a 
                      href="https://apps.apple.com/us/app/napa-valley-winery-finder-real/id419570092" 
                      rel="noopener noreferrer"
                      
                      className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      <span className="font-medium">App Store</span>
                    </a>
                    <a 
                      href="https://play.google.com/store/apps/details?id=com.shoutem.n75202&hl=en" 
                      rel="noopener noreferrer"
                      
                      className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition-colors"
                    >
                      <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.18 2.81L14.24 12 3.18 21.19c-.2-.23-.32-.53-.32-.85V3.66c0-.32.12-.62.32-.85zm1.04-1.08L15.63 12 4.22 22.27c-.17.06-.36.09-.56.09-.23 0-.45-.06-.65-.16l11.48-9.91L3.01 2.38c.2-.1.42-.16.65-.16.2 0 .39.03.56.09v-.58zm15.96 9.19L17.68 12l2.5 1.08c.45.19.75.62.75 1.12 0 .5-.3.93-.75 1.12l-2.5 1.08 2.5 1.08c.45.19.75.62.75 1.12s-.3.93-.75 1.12L5.34 24c-.2.09-.42.13-.65.13-.5 0-.97-.2-1.3-.54L14.87 12 3.39.41C3.72.14 4.19-.06 4.69-.06c.23 0 .45.04.65.13l14.84 6.24c.45.19.75.62.75 1.12 0 .5-.3.93-.75 1.12l-2.5 1.08 2.5 1.08c.45.19.75.62.75 1.12 0 .5-.3.93-.75 1.13z"/>
                      </svg>
                      <span className="font-medium">Google Play</span>
                    </a>
                  </div>
                </div>
              )}

              {/* Embedded YouTube Video Placeholder */}
              <div className="mb-8">
                <h2 className="font-serif text-2xl text-primary mb-4">Winery Tour</h2>
                <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
                  <div className="text-center text-muted-foreground">
                    <p className="text-sm">Video content coming soon</p>
                    <p className="text-xs mt-1">Check YouTube for "{winery.name} tour"</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-muted/50 rounded-lg p-6 sticky top-24">
                <h3 className="font-serif text-xl text-primary mb-4">Visit Information</h3>
                
                <div className="space-y-4">
                  {winery.address && (
                    <div className="flex items-start gap-3">
                      <MapPin size={20} className="text-primary flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">Address</p>
                        <p className="text-muted-foreground text-sm">{winery.address}</p>
                      </div>
                    </div>
                  )}

                  {winery.phone && (
                    <div className="flex items-start gap-3">
                      <Phone size={20} className="text-primary flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">Phone</p>
                        <a href={`tel:${winery.phone}`} className="text-muted-foreground text-sm hover:text-primary">
                          {winery.phone}
                        </a>
                      </div>
                    </div>
                  )}

                  {winery.website && (
                    <div className="flex items-start gap-3">
                      <Globe size={20} className="text-primary flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium">Website</p>
                        <a 
                          href={winery.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-muted-foreground text-sm hover:text-primary break-all"
                        >
                          Visit Website
                        </a>
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    <Clock size={20} className="text-primary flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-medium">Appointments</p>
                      <p className="text-muted-foreground text-sm">
                        {winery.appointment_required 
                          ? 'Please call ahead for appointment' 
                          : 'No appointment needed'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-3">
                  <a
                    href={getDirectionsUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary w-full"
                    data-testid="get-directions-btn"
                  >
                    <NavigationArrow size={18} className="mr-2" />
                    Get Directions
                  </a>
                  
                  {winery.website && (
                    <a
                      href={winery.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary w-full"
                      data-testid="visit-website-btn"
                    >
                      Visit Website
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Viator nearby experiences */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <ViatorWidget region={winery?.region || 'Napa Valley'} />
      </section>
    </>
  );
};

export default WineryDetail;
